import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Typography, Box } from '@mui/material';
import gsap from "gsap";

function Accessibilita() {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pageRef.current) {
      gsap.fromTo(
        pageRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" }
      );
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Dichiarazione di Accessibilità | CAMILLACINODESIGN&DEV</title>
        <meta name="description" content="Dichiarazione di accessibilità del sito camillacinodesign.dev ai sensi della Direttiva (UE) 2019/882 (European Accessibility Act)." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box
        ref={pageRef}
        sx={{
          minHeight: '100vh',
          pt: { xs: 14, md: 18 },
          pb: 10,
          backgroundColor: '#101010',
          color: '#fff',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 2,
              background: 'linear-gradient(135deg, #048091, #7E82F7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Dichiarazione di Accessibilità
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              color: 'rgba(255,255,255,0.5)',
              mb: 6,
            }}
          >
            Ultimo aggiornamento: 21 maggio 2026
          </Typography>

          {/* Sezione 1 */}
          <Section title="1. Ambito di applicazione">
            <p>
              La presente dichiarazione si applica al sito web <strong>camillacinodesign.dev</strong>, di proprietà di
              Camilla Cino - CAMILLACINODESIGN&DEV, con sede legale in via Per S. Felice 3, Camposanto (MO) 41031,
              P.IVA 04601200167.
            </p>
            <p>
              La dichiarazione è redatta ai sensi della <strong>Direttiva (UE) 2019/882</strong> (European Accessibility Act),
              recepita in Italia con il <strong>D.Lgs. 82/2022</strong>, e delle <strong>Linee Guida AgID</strong> sull'accessibilità
              degli strumenti informatici.
            </p>
          </Section>

          {/* Sezione 2 */}
          <Section title="2. Standard di riferimento">
            <p>
              Il sito è stato progettato con l'obiettivo di conformarsi allo standard <strong>WCAG 2.1 livello AA</strong> (Web Content Accessibility Guidelines)
              e alla norma tecnica <strong>EN 301 549</strong>.
            </p>
          </Section>

          {/* Sezione 3 */}
          <Section title="3. Stato di conformità">
            <p>
              Il sito <strong>camillacinodesign.dev</strong> è <strong>parzialmente conforme</strong> ai requisiti di accessibilità previsti dalle normative vigenti.
              Le non conformità e le eccezioni note sono elencate di seguito.
            </p>
          </Section>

          {/* Sezione 4 */}
          <Section title="4. Contenuti non accessibili">
            <p>I contenuti elencati di seguito non sono accessibili per i motivi indicati:</p>

            <SubSection title="4.1 Non conformità con la normativa">
            <ul>
              <li>
                Alcune animazioni decorative (particelle, transizioni di pagina) potrebbero non rispettare
                pienamente il criterio WCAG 2.3.3 (Animazione da interazioni). È in corso l'implementazione
                del supporto alla media query <code>prefers-reduced-motion</code>.
              </li>
              <li>
                Il widget chatbot potrebbe non essere completamente navigabile tramite tastiera
                in tutti i browser.
              </li>
            </ul>
            </SubSection>

            <SubSection title="4.2 Onere sproporzionato">
            <p>
              Al momento non viene invocata alcuna esenzione per onere sproporzionato.
            </p>
            </SubSection>

            <SubSection title="4.3 Contenuti di terze parti">
            <ul>
              <li>
                Il calendario di prenotazione nella pagina Contatti è sviluppato internamente
                con attenzione alla navigazione da tastiera e compatibilità con screen reader.
              </li>
              <li>
                Il cookie banner è gestito internamente con cookie tecnici minimi.
              </li>
            </ul>
            </SubSection>
          </Section>

          {/* Sezione 5 */}
          <Section title="5. Requisiti soddisfatti">
            <ul>
              <li>Struttura semantica HTML con heading gerarchici e landmark ARIA.</li>
              <li>Testi alternativi (<code>alt</code>) sulle immagini informative.</li>
              <li>Contrasto colore conforme al livello AA per testi e componenti interattivi.</li>
              <li>Layout responsive e leggibile su dispositivi mobili e desktop.</li>
              <li>Link e pulsanti con etichette accessibili (<code>aria-label</code>).</li>
              <li>Gestione del focus visibile sugli elementi interattivi.</li>
            </ul>
          </Section>

          {/* Sezione 6 */}
          <Section title="6. Modalità di redazione">
            <p>
              La presente dichiarazione è stata redatta il <strong>21 maggio 2026</strong> sulla base
              di un'autovalutazione effettuata dalla titolare del sito.
            </p>
            <p>
              Gli strumenti utilizzati per la verifica includono: Lighthouse (Google Chrome DevTools),
              axe DevTools, WAVE e controllo manuale della navigazione da tastiera.
            </p>
          </Section>

          {/* Sezione 7 */}
          <Section title="7. Feedback e contatti">
            <p>
              Per segnalare problemi di accessibilità o richiedere contenuti in formato alternativo,
              è possibile contattare:
            </p>
            <Box
              sx={{
                backgroundColor: 'rgba(4, 128, 145, 0.1)',
                border: '1px solid rgba(4, 128, 145, 0.3)',
                borderRadius: 2,
                p: 3,
                mt: 2,
                mb: 2,
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>Camilla Cino</strong><br />
                Email: <a href="mailto:info@camillacinodesigndev.it" style={{ color: '#048091' }}>info@camillacinodesigndev.it</a><br />
                Sito: <a href="https://www.camillacinodesign.dev" style={{ color: '#048091' }}>www.camillacinodesign.dev</a>
              </p>
            </Box>
            <p>
              Il responsabile si impegna a rispondere entro <strong>30 giorni</strong> dalla ricezione della segnalazione.
            </p>
          </Section>

          {/* Sezione 8 */}
          <Section title="8. Procedura di attuazione">
            <p>
              In caso di risposta insoddisfacente o mancata risposta entro 30 giorni, l'utente può
              presentare una segnalazione all'<strong>AgID — Agenzia per l'Italia Digitale</strong> tramite
              il portale dedicato:{' '}
              <a
                href="https://www.agid.gov.it/it/design-servizi/accessibilita"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#048091' }}
              >
                www.agid.gov.it/it/design-servizi/accessibilita
              </a>
            </p>
          </Section>

        </Container>
      </Box>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 5 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 600,
          fontSize: { xs: '1.2rem', md: '1.4rem' },
          mb: 2,
          color: '#fff',
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: '0.95rem',
          lineHeight: 1.8,
          color: 'rgba(255,255,255,0.8)',
          '& p': { mb: 2, mt: 0 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 1 },
          '& code': {
            backgroundColor: 'rgba(126, 130, 247, 0.15)',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.85rem',
          },
          '& a': {
            color: '#048091',
            textDecoration: 'underline',
            '&:hover': { color: '#7E82F7' },
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 500,
          fontSize: '1rem',
          mb: 1,
          color: 'rgba(255,255,255,0.9)',
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default Accessibilita;
